import { useParams, useNavigate } from 'react-router-dom';
import { C, FONTS } from '../../tokens/design';
import { usePlatformMsp } from '../../hooks/usePlatformData';
import { useClient } from '../../context/ClientContext';
import { relativeTime, exactTime } from '../../utils/time';
import PlatformShell from '../../components/layout/PlatformShell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import Skeleton from '../../components/ui/Skeleton';
import { TH, TR, TD } from '../../components/ui/Table';
import BrandLogo from '../../components/ui/BrandLogo';

export default function PlatformMspDetail() {
  const { mspId } = useParams();
  const navigate = useNavigate();
  const { setClientId, setClientName } = useClient();
  const { data: msp, loading, error } = usePlatformMsp(mspId);

  if (loading) return (
    <PlatformShell title="Loading MSP...">
      <Button variant="ghost" onClick={() => navigate('/platform/dashboard')}>{'\u2190'} Back to Platform</Button>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 12 }}>
        <Card style={{ padding: 22 }}><Skeleton height={150} /></Card>
        <Card style={{ padding: 22 }}><Skeleton height={150} /></Card>
      </div>
    </PlatformShell>
  );

  if (error || !msp) return (
    <PlatformShell title="MSP Not Found">
      <Button variant="ghost" onClick={() => navigate('/platform/dashboard')}>{'\u2190'} Back to Platform</Button>
      <div style={{ padding: 32, textAlign: 'center', color: C.t2, fontFamily: FONTS.mono }}>
        {error || `MSP ${mspId} not found.`}
      </div>
    </PlatformShell>
  );

  const name = msp.name || msp.companyName || mspId;
  const tier = msp.tier || msp.partnerTier || 'Standard';
  const status = msp.status || 'Active';
  const isActive = status === 'Active' || status === 'active';
  const owner = msp.owner || msp.ownerName || '\u2014';
  const ownerEmail = msp.ownerEmail || '';
  const created = msp.createdAt || null;
  const clients = msp.clients || [];
  const team = msp.team || msp.teamMembers || [];
  const recentScans = msp.recentScans || [];
  const tierColor = tier === 'Flagship' ? '#9333ea' : tier === 'Growth' ? '#ca8a04' : '#2563eb';

  return (
    <PlatformShell title={name} sub={`MSP Partner \u2014 ${tier}`}>
      <div style={{ marginBottom: 12 }}>
        <Button variant="ghost" onClick={() => navigate('/platform/dashboard')}>{'\u2190'} Back to Platform Dashboard</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        {/* MSP Info */}
        <Card style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 14 }}>MSP Information</div>
          {[
            { l: 'Name', v: name },
            { l: 'Partner Tier', v: tier, badge: true },
            { l: 'Status', v: status },
            { l: 'Owner', v: `${owner}${ownerEmail ? ` (${ownerEmail})` : ''}` },
            { l: 'Created', v: created ? exactTime(created) : '\u2014' },
            { l: 'MSP ID', v: mspId },
          ].map(r => (
            <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.b2}` }}>
              <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{r.l}</span>
              {r.badge ? (
                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, fontFamily: FONTS.mono, color: tierColor, background: `${tierColor}14`, border: `1px solid ${tierColor}33` }}>{r.v}</span>
              ) : (
                <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, fontWeight: 600 }}>{r.v}</span>
              )}
            </div>
          ))}
        </Card>

        {/* Branding Preview */}
        <Card style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 14 }}>Branding Preview</div>
          <div style={{ padding: 20, borderRadius: 8, background: msp.backgroundColor || C.bg, border: `1px solid ${C.b1}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              {msp.logoUrl ? (
                <BrandLogo
                  src={msp.logoUrl}
                  alt={`${msp.portalName || name} logo`}
                  letter={msp.logoLetter || name[0] || 'M'}
                  primaryColor={msp.primaryColor || '#ef4444'}
                  secondaryColor={msp.secondaryColor || '#7c3aed'}
                  width={120}
                  height={42}
                  maxWidth={120}
                  maxHeight={42}
                />
              ) : (
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: `linear-gradient(135deg, ${msp.primaryColor || '#ef4444'}, ${msp.secondaryColor || '#7c3aed'})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 900, color: '#fff',
                }}>{msp.logoLetter || name[0] || 'M'}</div>
              )}
              <span style={{ fontSize: 16, fontWeight: 800, color: msp.textColor || C.t1, fontFamily: FONTS.sans }}>
                {msp.portalName || name}
              </span>
            </div>
            <div style={{ fontSize: 10, color: msp.textColor || C.t2, fontFamily: FONTS.mono, opacity: 0.7 }}>
              {msp.tagline || 'Security Command Portal'}
            </div>
          </div>
        </Card>
      </div>

      {/* Team Members */}
      <Card style={{ marginBottom: 14 }}>
        <CardHeader title="Team Members" action={<span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{team.length} members</span>} />
        {team.length === 0 ? (
          <div style={{ padding: '20px 14px', textAlign: 'center', fontSize: 11, color: C.t3, fontFamily: FONTS.mono }}>No team members data available.</div>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TH cols={['Name', 'Email', 'Role', 'Status']} />
            <tbody>
              {team.map((t, i) => (
                <tr key={t.id || i} style={{ borderBottom: `1px solid ${C.b2}` }}>
                  <TD style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{t.name || '\u2014'}</TD>
                  <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{t.email || '\u2014'}</TD>
                  <TD><Tag label={t.role || 'Viewer'} color={t.role === 'Admin' ? C.pur : C.sky} /></TD>
                  <TD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: t.status === 'Active' ? C.grn : C.t3 }} />
                      <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{t.status || 'Active'}</span>
                    </div>
                  </TD>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Client List */}
      <Card style={{ marginBottom: 14 }}>
        <CardHeader title="Clients" action={<span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{clients.length} clients</span>} />
        {clients.length === 0 ? (
          <div style={{ padding: '20px 14px', textAlign: 'center', fontSize: 11, color: C.t3, fontFamily: FONTS.mono }}>No clients onboarded for this MSP.</div>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TH cols={['Client', 'Score', 'Findings', 'Status']} />
            <tbody>
              {clients.map((c, i) => {
                const score = c.score ?? null;
                const sc = typeof score === 'number' ? (score >= 80 ? C.grn : score >= 60 ? C.amb : C.red) : C.t3;
                return (
                  <TR key={c.clientId || i} onClick={() => {
                    if (!c.clientId) return;
                    setClientId(c.clientId);
                    setClientName(c.name || c.companyName || c.clientId);
                    navigate('/client/dashboard');
                  }}>
                    <TD style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{c.name || c.companyName || '\u2014'}</TD>
                    <TD><span style={{ fontSize: 14, fontWeight: 800, color: sc, fontFamily: FONTS.mono }}>{typeof score === 'number' ? score : '\u2014'}</span></TD>
                    <TD style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>{c.openFindings ?? c.findings ?? '\u2014'}</TD>
                    <TD><Tag label={c.status || 'Active'} color={C.grn} /></TD>
                  </TR>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* Recent Scans */}
      <Card>
        <CardHeader title="Recent Scan Activity" action={<span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{recentScans.length} scans</span>} />
        {recentScans.length === 0 ? (
          <div style={{ padding: '20px 14px', textAlign: 'center', fontSize: 11, color: C.t3, fontFamily: FONTS.mono }}>No recent scan activity.</div>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TH cols={['Client', 'Profile', 'Status', 'Started', 'Duration']} />
            <tbody>
              {recentScans.slice(0, 10).map((s, i) => (
                <tr key={s.scanId || i} style={{ borderBottom: `1px solid ${C.b2}` }}>
                  <TD style={{ fontSize: 11, color: C.t1 }}>{s.clientName || '\u2014'}</TD>
                  <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{s.profileName || 'full'}</TD>
                  <TD><Tag label={s.status || 'Completed'} color={s.status === 'Running' ? C.amb : C.grn} /></TD>
                  <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }} title={s.startedAt ? exactTime(s.startedAt) : ''}>{s.startedAt ? relativeTime(s.startedAt) : '\u2014'}</TD>
                  <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{s.duration || '\u2014'}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </PlatformShell>
  );
}
